/**
 * Arbor: a generic ceremony arch placement marker.
 * Dimensions: ARBOR_WIDTH_FT x ARBOR_DEPTH_FT from domain/inventory.ts.
 * Default 6ft wide x 2ft deep — confirm with venue.
 * has_stock_limit=false, no inventory enforcement.
 * x/y stored at CENTER (consistent with all feature-zone objects).
 */

import { Group, Rect, Text } from 'react-konva';
import { useState } from 'react';
import type Konva from 'konva';
import type { LayoutObject, ObjectCatalogEntry } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

interface ArborProps {
  object: LayoutObject;
  catalogEntry: ObjectCatalogEntry;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  roomRotationDegrees: number;
}

export function Arbor({ object, catalogEntry, isSelected, onSelect, onDragEnd, roomRotationDegrees }: ArborProps) {
  const widthPx = catalogEntry.defaultWidthFt * FEET_TO_PX;
  const heightPx = catalogEntry.defaultHeightFt * FEET_TO_PX;
  const [isDragging, setIsDragging] = useState(false);

  function handleDragStart(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(true);
    const c = e.target.getStage()?.container();
    if (c) c.style.cursor = 'grabbing';
  }

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(false);
    const c = e.target.getStage()?.container();
    if (c) c.style.cursor = 'grab';
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : 1}
      onMouseEnter={(e) => {
        const c = e.target.getStage()?.container();
        if (c) c.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const c = e.target.getStage()?.container();
        if (c) c.style.cursor = 'default';
      }}
    >
      <Rect
        x={-widthPx / 2}
        y={-heightPx / 2}
        width={widthPx}
        height={heightPx}
        cornerRadius={4}
        fill="#EEF2EF"
        stroke={isSelected ? '#C9A35C' : '#5F8767'}
        strokeWidth={isSelected ? 3 : 2}
        dash={[5, 3]}
        shadowColor="#2A2522"
        shadowOpacity={isDragging ? 0 : 0.1}
        shadowBlur={isDragging ? 0 : 5}
        shadowOffsetY={isDragging ? 0 : 2}
      />
      <Text
        text={object.label}
        fontSize={10}
        fontFamily="Inter, sans-serif"
        fill="#3D5F44"
        width={widthPx}
        height={heightPx}
        offsetX={widthPx / 2}
        offsetY={heightPx / 2}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
      {object.locked && (
        <Text text={'\u{1F512}'} fontSize={11} x={0} y={-(heightPx / 2 + 12)} offsetX={5.5} offsetY={5.5} listening={false} />
      )}
    </Group>
  );
}
